import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../components/AuthProvider';
import {
  getRequestableClients, getListRequests, createListRequest,
  getSourcingStrategies, decideStrategy, getStagedListsInfo, getStagedContacts, rejectStagedContacts, decideStagedList,
  type AssignableClient, type ListRequest, type SourcingStrategy, type StagedListInfo, type StagedContactSample,
} from '../../lib/refonteApi';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const CHANNELS = [['cold_call', 'Cold call'], ['mailing', 'Mail'], ['linkedin', 'LinkedIn'], ['gtm', 'GTM']];
const fmtDate = (s: string | null) => s ? new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';

// Étapes dérivées du trio demande + stratégie + liste stagée (design machine à listes 28/09).
type Stage = 'demande' | 'gate1' | 'sourcing' | 'gate2' | 'enrich' | 'livre';
const STAGES: [Stage, string, string][] = [
  ['demande', 'Demandé', 'bg-st-warn/10 text-st-warn'],
  ['gate1', 'Stratégie à valider', 'bg-hook-rose/10 text-hook-rose'],
  ['sourcing', 'Sourcing (machine)', 'bg-st-cy/10 text-st-cy'],
  ['gate2', 'Liste à valider', 'bg-hook-rose/10 text-hook-rose'],
  ['enrich', 'Enrichissement & push', 'bg-st-cy/10 text-st-cy'],
  ['livre', 'Livré (uploadé)', 'bg-st-ok/10 text-st-ok'],
];
const ENRICH_STATUSES = ['validated', 'enriching', 'enriched', 'pushing'];

function stageOf(r: ListRequest, strat: SourcingStrategy | undefined, list: StagedListInfo | undefined): Stage {
  // La liste la plus récente prime sur le statut de la demande : une demande peut avoir été
  // « validée/livrée » par une liste précédente alors qu'une NOUVELLE liste attend sa Gate 2.
  if (list) {
    if (list.status === 'pushed') return 'livre';
    if (ENRICH_STATUSES.includes(list.status)) return 'enrich';
    if (list.status === 'staged' && list.contactCount > 0) return 'gate2';
  }
  if (r.status === 'livre') return 'livre';
  if (r.status === 'validee') return 'enrich';
  if (r.status === 'a_valider') return 'gate2';
  if (r.status === 'strategie_validee' || r.status === 'sourcing' || r.status === 'en_cours') return 'sourcing';
  if (r.status === 'strategie_proposee' || strat?.status === 'proposee') return 'gate1';
  return 'demande';
}

const LIST_STATUS_FR: Record<string, string> = {
  validated: 'validée — en file d’enrichissement', enriching: 'enrichissement FullEnrich en cours',
  enriched: 'enrichie — push Pipedrive imminent', pushing: 'push Pipedrive en cours', pushed: 'poussée dans Pipedrive',
};

const StrategyDetail: React.FC<{ s: SourcingStrategy }> = ({ s }) => {
  const plan = s.planSourcing || {};
  const Section: React.FC<{ t: string; children: React.ReactNode }> = ({ t, children }) => (
    <div className="mb-2"><div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-0.5">{t}</div><div className="text-[12px] text-dim whitespace-pre-wrap">{children}</div></div>
  );
  return (
    <div className="mt-2 border-t border-line pt-2">
      {s.objectif && <Section t="Objectif">{s.objectif}</Section>}
      {s.analyseMission && <Section t="Mission & contrat">{s.analyseMission}</Section>}
      {s.analyseCalls && <Section t="Ce que disent les calls">{s.analyseCalls}</Section>}
      {s.etatDesLieux && <Section t="État des lieux du pipe">{s.etatDesLieux}</Section>}
      {plan && (plan.net_new || plan.recyclage) && (
        <Section t="Plan de sourcing">
          {plan.recyclage?.action && <>♻️ Recyclage : {plan.recyclage.action}{plan.recyclage.volume_estime ? ` (~${plan.recyclage.volume_estime})` : ''}{'\n'}</>}
          {(plan.net_new || []).map((n: any, i: number) => (
            <React.Fragment key={i}>🆕 {n.cibles || '—'} — {(n.personas || []).join(', ')} · cible {n.volume_cible ?? '?'} · via {n.source || '—'}{n.signaux_obligatoires ? `\n   Signaux : ${n.signaux_obligatoires}` : ''}{'\n'}</React.Fragment>
          ))}
          {plan.exclusions?.length > 0 && <>⛔ Exclusions : {plan.exclusions.join(' · ')}{'\n'}</>}
          {plan.regles_qualite?.length > 0 && <>✅ Qualité : {plan.regles_qualite.join(' · ')}{'\n'}</>}
          {plan.label_pipedrive && <>🏷️ Label : {plan.label_pipedrive}</>}
        </Section>
      )}
    </div>
  );
};

const DemandeListePage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'sdr';
  // Gates réservées aux managers (Charles 29/09). Piège : les comptes SDR historiques ont role='admin'
  // mais portent prospecting_team (cold_call/gtm) — un admin AVEC équipe de prospection est un SDR.
  // Même règle côté RLS : fonction is_sourcing_manager() (migration sourcing_manager_gates).
  const canAdvance = ['csm', 'super_admin'].includes(role) || (role === 'admin' && !profile?.prospecting_team);
  const myName = profile?.full_name ?? profile?.email ?? 'CSM';

  const [clients, setClients] = useState<AssignableClient[]>([]);
  const [rows, setRows] = useState<ListRequest[]>([]);
  const [strats, setStrats] = useState<SourcingStrategy[]>([]);
  const [lists, setLists] = useState<StagedListInfo[]>([]);
  const [samples, setSamples] = useState<Record<string, StagedContactSample[]>>({});
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);

  // Formulaire
  const [clientId, setClientId] = useState('');
  const [volume, setVolume] = useState('');
  const [deadline, setDeadline] = useState('');
  const [channel, setChannel] = useState('cold_call');
  const [sectors, setSectors] = useState('');
  const [sizes, setSizes] = useState('');
  const [functions, setFunctions] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [cs, rs, ss, ls] = await Promise.all([getRequestableClients(), getListRequests(), getSourcingStrategies(), getStagedListsInfo()]);
      setClients(cs); setRows(rs); setStrats(ss); setLists(ls);
      // Contacts complets des listes en attente de validation (Gate 2 : sélection individuelle)
      const toSample = ls.filter(l => l.status === 'staged' && l.contactCount > 0).slice(0, 8);
      const pairs = await Promise.all(toSample.map(async l => [l.id, await getStagedContacts(l.id)] as const));
      setSamples(Object.fromEntries(pairs));
      // par défaut tout est sélectionné
      const s: Record<string, boolean> = {};
      for (const [, cs2] of pairs) for (const c of cs2) s[c.id] = true;
      setSel(s);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const stratByReq = useMemo(() => {
    // strats est trié du plus récent au plus ancien : on garde la plus récente,
    // sauf si elle est rejetée et qu'une non-rejetée existe (elle prime pour l'affichage Gate 1).
    const m: Record<string, SourcingStrategy> = {};
    for (const s of strats) {
      if (!s.requestId) continue;
      const cur = m[s.requestId];
      if (!cur) m[s.requestId] = s;
      else if (cur.status === 'rejetee' && s.status !== 'rejetee') m[s.requestId] = s;
    }
    return m;
  }, [strats]);
  const listByReq = useMemo(() => {
    const m: Record<string, StagedListInfo> = {};
    for (const l of lists) if (l.requestId && !m[l.requestId]) m[l.requestId] = l; // plus récente d'abord
    return m;
  }, [lists]);

  const grouped = useMemo(() => {
    const g: Record<Stage, ListRequest[]> = { demande: [], gate1: [], sourcing: [], gate2: [], enrich: [], livre: [] };
    for (const r of rows) {
      if (r.status === 'rejetee') continue;
      g[stageOf(r, stratByReq[r.id], listByReq[r.id])].push(r);
    }
    return g;
  }, [rows, stratByReq, listByReq]);

  const submit = async () => {
    setErr(null); setOk(false);
    if (!clientId) { setErr('Choisis un client.'); return; }
    if (!volume) { setErr('Indique un volume souhaité.'); return; }
    setSaving(true);
    try {
      const c = clients.find(x => x.id === clientId);
      await createListRequest({
        requesterId: profile?.user_id ?? '', requesterName: profile?.full_name ?? profile?.email ?? 'SDR',
        clientId, clientName: c?.name ?? null, volume: Number(volume) || null, deadline: deadline || null, channel,
        icpSectors: sectors.trim() || null, icpSizes: sizes.trim() || null, icpFunctions: functions.trim() || null, note: note.trim() || null,
      });
      setOk(true); setVolume(''); setDeadline(''); setSectors(''); setSizes(''); setFunctions(''); setNote('');
      await load();
    } catch (e: any) { setErr(e.message); }
    setSaving(false);
  };

  const gate1 = async (s: SourcingStrategy, decision: 'validee' | 'rejetee') => {
    const motif = decision === 'rejetee' ? (window.prompt('Motif du rejet (il nourrit la prochaine stratégie) :') || '') : undefined;
    if (decision === 'rejetee' && !motif) return;
    try { await decideStrategy(s, decision, myName, motif); await load(); } catch (e: any) { setErr(e.message); }
  };
  const gate2 = async (l: StagedListInfo, decision: 'validated' | 'rejected') => {
    const contacts = samples[l.id] || [];
    const keep = contacts.filter(c => sel[c.id]);
    const drop = contacts.filter(c => !sel[c.id]);
    if (decision === 'rejected') {
      const motif = window.prompt('Motif du rejet de la liste entière :') || '';
      if (!motif) return;
      try { await decideStagedList(l, 'rejected', motif); await load(); } catch (e: any) { setErr(e.message); }
      return;
    }
    if (!keep.length) { setErr('Aucun contact sélectionné — coche au moins un contact ou rejette la liste.'); return; }
    if (!window.confirm(`Valider ${keep.length}/${contacts.length} contacts de ${l.clientName} ?${drop.length ? ` Les ${drop.length} décochés sont écartés (ni enrichis ni poussés).` : ''} L'enrichissement FullEnrich (~${keep.length * 10} crédits) puis le push Pipedrive partent automatiquement.`)) return;
    try {
      if (drop.length) {
        const motif = window.prompt(`Motif d'écartement des ${drop.length} contacts décochés (nourrit les règles du playbook) :`) || 'écarté à la review';
        await rejectStagedContacts(drop.map(c => c.id), motif);
      }
      await decideStagedList(l, 'validated');
      await load();
    } catch (e: any) { setErr(e.message); }
  };

  const field = 'w-full rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose';
  const btn = 'text-[11px] font-semibold px-2 py-1 rounded border whitespace-nowrap';

  return (
    <div>
      <h1 className="text-[21px] font-bold tracking-tight mb-1">Demande de liste</h1>
      <p className="text-dim text-[13px] mb-4">Chaque demande passe par une stratégie proposée par la machine et validée ici (Gate 1), puis la liste sourcée revient pour validation (Gate 2) avant enrichissement et upload Pipedrive avec son label.</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Formulaire */}
        <div className="bg-panel border border-line rounded-card p-4 h-max">
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mb-3">Nouvelle demande</h2>
          <div className="space-y-2.5">
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-faint block mb-1">Client *</label>
              <select value={clientId} onChange={e => setClientId(e.target.value)} className={field}>
                <option value="">— choisir —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div><label className="font-mono text-[9px] uppercase tracking-widest text-faint block mb-1">Volume *</label><input type="number" value={volume} onChange={e => setVolume(e.target.value)} placeholder="300" className={field} /></div>
              <div><label className="font-mono text-[9px] uppercase tracking-widest text-faint block mb-1">Deadline</label><input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className={field} /></div>
            </div>
            <div>
              <label className="font-mono text-[9px] uppercase tracking-widest text-faint block mb-1">Canal</label>
              <div className="flex flex-wrap gap-1">{CHANNELS.map(([v, l]) => <button key={v} onClick={() => setChannel(v)} className={`text-[12px] px-2.5 py-1 rounded-md border ${channel === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>)}</div>
            </div>
            <div className="border-t border-line pt-2.5">
              <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">Critères ICP</div>
              <input value={sectors} onChange={e => setSectors(e.target.value)} placeholder="Secteurs (ex. SaaS, industrie…)" className={`${field} mb-2`} />
              <input value={sizes} onChange={e => setSizes(e.target.value)} placeholder="Tailles (ex. 51-200, 200-500…)" className={`${field} mb-2`} />
              <input value={functions} onChange={e => setFunctions(e.target.value)} placeholder="Fonctions / persona (ex. DAF, DSI…)" className={field} />
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Note libre (exclusions, contexte, « net-new obligatoire », fichier fourni…)" rows={2} className={field} />
            {ok && <p className="text-[12px] text-st-ok">✓ Demande envoyée — la machine propose une stratégie (run de nuit, ou en journée si urgent).</p>}
            <button onClick={submit} disabled={saving} className="w-full text-[13px] font-semibold px-3 py-2 rounded-md bg-hook-rose text-white hover:opacity-90 disabled:opacity-50">{saving ? '…' : 'Envoyer la demande'}</button>
          </div>
        </div>

        {/* Pipeline */}
        <div>
          {loading ? <SkeletonRows count={5} /> : rows.length === 0 ? (
            <EmptyState title="Aucune demande" hint="Crée la première avec le formulaire." />
          ) : (
            <div className="space-y-4">
              {STAGES.map(([st, label, tone]) => (
                <div key={st} className="bg-panel border border-line rounded-card p-4">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${tone}`}>{label}</span>
                    <span className="font-mono text-[11px] text-faint">{grouped[st].length}</span>
                  </div>
                  {grouped[st].length === 0 ? <p className="text-[12px] text-faint italic">Rien ici.</p> : (
                    <div className="divide-y divide-line">
                      {grouped[st].map(r => {
                        const s = stratByReq[r.id];
                        const l = listByReq[r.id];
                        return (
                          <div key={r.id} className="py-2.5">
                            <div className="flex items-start gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-semibold">{r.clientName || '—'} <span className="text-dim font-normal">· {l?.contactCount || r.volume || '?'} contacts</span>{l?.pipedriveLabel && <span className="ml-1.5 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn">🏷 {l.pipedriveLabel}</span>}</div>
                                <div className="font-mono text-[10px] text-faint">
                                  {r.requesterName} · deadline {fmtDate(r.deadline)} · {CHANNELS.find(c => c[0] === r.channel)?.[1] || r.channel || '—'}
                                </div>
                                {(r.icpSectors || r.icpSizes || r.icpFunctions) && (
                                  <div className="text-[11px] text-dim mt-0.5">{[r.icpFunctions, r.icpSectors, r.icpSizes].filter(Boolean).join(' · ')}</div>
                                )}

                                {st === 'demande' && <div className="text-[11px] text-faint italic mt-0.5">Stratégie en préparation par la machine (run de nuit ~0h45, ou déclenchement manuel).</div>}

                                {st === 'gate1' && s && (
                                  <>
                                    <button onClick={() => setOpen(o => ({ ...o, [s.id]: !o[s.id] }))} className="text-[11px] text-hook-rose font-semibold mt-1">{open[s.id] ? '▾ Replier la stratégie' : '▸ Lire la stratégie proposée'}</button>
                                    {open[s.id] && <StrategyDetail s={s} />}
                                  </>
                                )}

                                {st === 'sourcing' && <div className="text-[11px] text-faint italic mt-0.5">Stratégie validée — sourcing par l'agent (identités + signaux, pas d'enrichissement payant à ce stade).</div>}

                                {st === 'gate2' && l && samples[l.id] && (() => {
                                  const cs2 = samples[l.id];
                                  const nSel = cs2.filter(c => sel[c.id]).length;
                                  const setAll = (v: boolean) => setSel(prev => { const n = { ...prev }; cs2.forEach(c => { n[c.id] = v; }); return n; });
                                  return (
                                    <div className="mt-1.5 border border-line rounded-md overflow-hidden">
                                      <div className="px-2 py-1 text-[10px] bg-line/10 flex items-center gap-2">
                                        <span className="font-semibold">{nSel}/{cs2.length} sélectionnés</span>
                                        <button onClick={() => setAll(true)} className="text-hook-rose font-semibold">tout cocher</button>
                                        <button onClick={() => setAll(false)} className="text-dim font-semibold">tout décocher</button>
                                        <span className="text-faint ml-auto">trié par score — coche ce qui part à l'enrichissement</span>
                                      </div>
                                      <div className="max-h-72 overflow-y-auto">
                                        <table className="w-full text-[11px]">
                                          <tbody>
                                            {cs2.map((c, i) => (
                                              <tr key={c.id} className={`${i % 2 ? 'bg-panel' : 'bg-line/20'} ${sel[c.id] ? '' : 'opacity-40'}`}>
                                                <td className="px-2 py-1"><input type="checkbox" checked={!!sel[c.id]} onChange={e => setSel(prev => ({ ...prev, [c.id]: e.target.checked }))} /></td>
                                                <td className="px-2 py-1 font-semibold whitespace-nowrap">{c.linkedin ? <a href={c.linkedin} target="_blank" rel="noreferrer" className="hover:text-hook-rose">{c.firstname} {c.lastname}</a> : <>{c.firstname} {c.lastname}</>}</td>
                                                <td className="px-2 py-1 text-dim">{c.jobTitle || '—'}</td>
                                                <td className="px-2 py-1">{c.company}</td>
                                                <td className="px-2 py-1 text-faint">{c.signal || ''}</td>
                                                <td className="px-2 py-1 text-faint whitespace-nowrap">{c.city || ''}{c.score != null ? ` · ${c.score}` : ''}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  );
                                })()}

                                {st === 'enrich' && l && (
                                  <div className="text-[11px] text-dim mt-0.5">
                                    {LIST_STATUS_FR[l.status] || l.status}
                                    {l.report && <> · {l.report.created ?? 0} deals créés · {l.report.skipped ?? 0} déjà en pipe · {l.report.no_phone ?? 0} sans tél</>}
                                  </div>
                                )}

                                {st === 'livre' && l?.report && (
                                  <div className="text-[11px] text-st-ok mt-0.5">✓ {l.report.created ?? 0} deals créés{l.report.skipped ? ` · ${l.report.skipped} dédup` : ''}{l.report.no_phone ? ` · ${l.report.no_phone} sans tél` : ''} — filtre Minari : label {l.pipedriveLabel || '—'}</div>
                                )}

                                {/* Traçabilité (demande Quentin 29/09) : comment la liste a été construite, sources, écarts vs demande */}
                                {l && (l.digestNotes || l.counts) && ['gate2', 'enrich', 'livre'].includes(st) && (
                                  <details className="mt-1.5">
                                    <summary className="text-[11px] text-hook-rose font-semibold cursor-pointer select-none">▸ Comment cette liste a été construite</summary>
                                    <div className="mt-1 border border-line rounded-md p-2 bg-line/10 max-h-56 overflow-y-auto">
                                      {l.counts && (
                                        <div className="text-[11px] text-dim mb-1.5">
                                          {Object.entries(l.counts as Record<string, any>)
                                            .filter(([, v]) => ['number', 'string'].includes(typeof v))
                                            .map(([k, v]) => `${k.replace(/_/g, ' ')} : ${v}`).join(' · ')}
                                        </div>
                                      )}
                                      {l.digestNotes && <div className="text-[11px] text-faint whitespace-pre-wrap">{l.digestNotes}</div>}
                                    </div>
                                  </details>
                                )}

                                {r.note && <div className="text-[11px] text-faint italic mt-0.5">{r.note}</div>}
                              </div>

                              {canAdvance && st === 'gate1' && s && (
                                <div className="flex gap-1.5">
                                  <button onClick={() => gate1(s, 'validee')} className={`${btn} border-st-ok text-st-ok hover:bg-st-ok/10`}>✓ Valider la stratégie</button>
                                  <button onClick={() => gate1(s, 'rejetee')} className={`${btn} border-st-bad text-st-bad hover:bg-st-bad/10`}>✕ Rejeter</button>
                                </div>
                              )}
                              {canAdvance && st === 'gate2' && l && (
                                <div className="flex gap-1.5">
                                  <button onClick={() => gate2(l, 'validated')} className={`${btn} border-st-ok text-st-ok hover:bg-st-ok/10`}>✓ Valider la sélection → enrich & push</button>
                                  <button onClick={() => gate2(l, 'rejected')} className={`${btn} border-st-bad text-st-bad hover:bg-st-bad/10`}>✕ Rejeter tout</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemandeListePage;
